import {CHANNELS} from "data/products";
import {getProduct, getProductBySlug} from "data/products/index";
import {PLACEHOLDERS} from "data/constants";
import {encodeInlineSvg} from "data/inline-svg";
import {getDefaultEdits} from "hooks/useEnsureDefaultEdits";
import {type TemplateDefinition} from "data/template-catalog";
import {resolveBannerSelection, type BannerSelection} from "data/template-availability";
import {bannerFromLegacyScene, CONTENT_FIELDS, type LegacyScene} from "../../../grid/legacy";
import {type BannerInstance} from "../../../grid/document";

/** The flat dark grey that every preview in the Templates tab shows behind the texts. */
export const PREVIEW_BASE = "2B2B2B";

/**
 * An empty, transparent image. A banner with it as its own background draws
 * the base colour only. A gallery number with no image would show the
 * background loader forever instead.
 */
const NO_IMAGE = encodeInlineSvg('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="9"/>');

/** Products to try first, so that most previews show a familiar product. */
const PREFERRED_PRODUCTS = ["JB", "IJ", "RD"];

export type PreviewSelection = BannerSelection & {layout: number};

/**
 * A product, content type, set and layout that draw the template. The first
 * preferred product that the template accepts wins, then the channel's order.
 */
export function previewSelection(definition: TemplateDefinition): PreviewSelection {
  const channel = CHANNELS.find((entry) => entry.value === definition.channel);
  if (!channel) throw new Error(`Unknown channel "${definition.channel}"`);
  const candidates: BannerSelection[] = [];
  for (const type of channel.types) {
    if (type.value === "action") continue;
    const codes = Object.keys(type.products);
    const ordered = [
      ...PREFERRED_PRODUCTS.filter((code) => codes.includes(code)),
      ...codes.filter((code) => !PREFERRED_PRODUCTS.includes(code)),
    ];
    for (const code of ordered) {
      const product = getProduct(code);
      if (!product || product.code === "ALL") continue;
      candidates.push({
        templateKey: definition.templateKey,
        product: product.slug,
        contentType: type.value,
        set: definition.set ?? null,
      });
    }
  }
  // A preferred product of a later content type beats a plain product of the first one.
  const rank = (selection: BannerSelection) => {
    const index = PREFERRED_PRODUCTS.indexOf(getProductBySlug(selection.product).code);
    return index === -1 ? PREFERRED_PRODUCTS.length : index;
  };
  const sorted = candidates
    .map((selection, order) => ({selection, order}))
    .sort((a, b) => rank(a.selection) - rank(b.selection) || a.order - b.order);
  for (const {selection} of sorted) {
    try {
      const resolved = resolveBannerSelection(selection);
      return {...selection, layout: resolved.layouts[0]};
    } catch {
      // Not drawn for this product or content type: try the next one.
    }
  }
  throw new Error(`No product draws template "${definition.templateKey}"`);
}

/**
 * The placeholder texts a new page of the channel opens with
 * (hooks/useEnsureDefaultEdits.js). A field the editor leaves unset stays
 * empty, so the template shows what it shows on a new page. A channel the
 * editor seeds nothing for gets the generic placeholders of the Grid.
 */
function placeholderScene(
  channel: string,
  productCode: string,
  productLabel: string,
  contentType: string
): LegacyScene {
  const placeholders = PLACEHOLDERS.en as Record<string, string>;
  const editorTexts = getDefaultEdits(channel, productCode, contentType, "en") as Record<
    string,
    string | undefined
  > | null;
  const scene: LegacyScene = {
    base: PREVIEW_BASE,
    bgOption: "custom",
    customBg: NO_IMAGE,
    tags: {"tag-0": "Tag 1", "tag-1": "Tag 2"},
  };
  for (const [field, legacy] of Object.entries(CONTENT_FIELDS)) {
    const text =
      field === "productName"
        ? productLabel
        : editorTexts
          ? (editorTexts[legacy] ?? "")
          : (placeholders[`${field}_${productCode}_${contentType}`] ?? placeholders[field] ?? "");
    scene[legacy] = {main: text};
  }
  return scene;
}

/** A read-only banner of the template: flat dark grey, placeholder texts, no images. */
export function previewBanner(definition: TemplateDefinition, instanceId: string): BannerInstance {
  const selection = previewSelection(definition);
  const {product} = resolveBannerSelection(selection);
  const label = "shortLabel" in product && product.shortLabel ? product.shortLabel : product.label;
  return bannerFromLegacyScene(
    {...selection, instanceId},
    placeholderScene(definition.channel, product.code, label, selection.contentType)
  );
}
